import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { WastageThresholdsApi } from '../services/wastageThresholdsApi';
import SettingsService from '../services/settingsService';
import UnitMappingService from '../services/unitMappingService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import './WastageThresholds.css';
import './StockInForm-module.css';
import { StandardCard, StandardSearch, StandardScrollStyles } from '../styles/standardStyles';
import InlineToggle from './InlineToggle';

// Usage-style dropdown copied to match StockIn exact styles
const UsageStyleDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select',
  style = {},
  className = ''
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);

  const normalized = options.map(opt =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );

  const current = normalized.find(o => o.value === value) || null;

  useEffect(() => {
    const onDocDown = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div
      className={`relative ${className}`}
      ref={ref}
      style={{ position: 'relative', width: '100%', ...(style || {}) }}
    >
      <div className="shift-select" onClick={() => setOpen(o => !o)}>
        <div className="input-text-wrapper">
          <span className="shift-details">
            {current ? (
              <>
                <span className="shift-name">{current.label}</span>
              </>
            ) : (
              <span className="shift-name" style={{ color: '#888' }}>{placeholder}</span>
            )}
          </span>
        </div>
        <FontAwesomeIcon 
          icon={faChevronDown} 
          style={{ 
            fontSize: '12px',
            color: '#000',
            marginLeft: '8px',
            flexShrink: 0
          }} 
        />
      </div>
      {open && (
        <div
          ref={menuRef}
          className="shift-dropdown-options"
          style={{ zIndex: 1000 }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e)=>e.stopPropagation()}
        >
          <div className="select-list" style={{ maxHeight: 200, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {normalized.map(opt => (
              <div
                key={String(opt.value)}
                className="shift-option"
                onClick={() => { onChange(opt.value); setOpen(false); }}
              >
                <span className="shift-name">{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Removed placeholder images; using text-only rows now

export default function WastageThresholds({ goTo }) {
  const [category, setCategory] = useState('All Categories');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productionTolerance, setProductionTolerance] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userId, setUserId] = useState(null);
  const searchInputRef = useRef(null);

  // Get user info from localStorage and fetch role from backend
  useEffect(() => {
    (async () => {
      try {
        const userStr = window.localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          setUserId(user.user_id || user.id);
          
          // Fetch role from backend since localStorage doesn't have it
          const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
          const response = await fetch(`${API_BASE_URL}/api/users/${user.user_id || user.id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'X-Business-Id': user.businessId || UnitMappingService.getBusinessId()
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            const roleName = userData.data?.role_name;
            if (roleName) {
              setUserRole(roleName.toLowerCase());
            }
          }
        }
      } catch (e) {
        console.error('[WastageThresholds] Failed to get user info:', e);
      }
    })();
  }, []);

  // Load production tolerance from settings
  useEffect(() => {
    (async () => {
      try {
        const businessId = UnitMappingService.getBusinessId();
        const res = await SettingsService.getSettings(businessId, ['wastage.yield_tolerance_pct']);
        const setting = (res.data || []).find(s => s.key === 'wastage.yield_tolerance_pct');
        if (setting) {
          setProductionTolerance(Number(setting.value));
        }
      } catch (e) {
        console.error('Failed to load production tolerance:', e);
      }
    })();
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const menuData = await WastageThresholdsApi.listMenuItemThresholds();
      
      // Filter out any undefined/null items and ensure required properties exist
      const validData = (menuData || []).filter(r => r && r.menu_item_id);
      
      setRows(validData.map(r => ({
        id: r.id || 0,
        menu_item_id: r.menu_item_id,
        name: r.dish_name || 'Unknown Item',
        category: r.category || 'Uncategorized',
        limit: r.threshold_quantity || 0,
        current: r.current_waste_quantity || 0,
        exceeded: r.is_exceeded || false,
        shouldNotify: r.should_notify || false,
        is_active: r.is_active !== undefined ? r.is_active : true,
        cooldown: r.notification_cooldown_minutes || 60,
        last_notification_at: r.last_notification_at || null
      })));
    } catch (e) { 
      console.error('[WastageThresholds] Load data error:', e);
      setError(e.message); 
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let base = rows;
    if (category !== 'All Categories') {
      base = base.filter(r => (r.category || '').toLowerCase() === category.toLowerCase());
    }
    if (!q) return base;
    return base.filter(r => r.name.toLowerCase().includes(q));
  }, [rows, query, category]);

  const onLimitChange = async (id, next) => {
    try {
      setRows(prev => prev.map(r => (r.menu_item_id === id ? { ...r, limit: next } : r)));
      
      // Get current row data
      const currentRow = rows.find(r => r.menu_item_id === id);
      
      // Check if user is owner/admin - use .includes() to handle "Owner/Admin", "Manager/Supervisor" etc.
      const isOwnerOrAdmin = userRole && (userRole.includes('owner') || userRole.includes('admin'));
      
      // If not owner/admin, send approval request instead of direct update
      if (userRole && !isOwnerOrAdmin) {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${API_BASE_URL}/api/wastage/threshold-approvals/request`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'X-Business-Id': UnitMappingService.getBusinessId()
          },
          body: JSON.stringify({
            userId: userId,
            threshold_type: 'menu_item',
            item_id: id,
            item_name: currentRow.name,
            current_threshold: currentRow.limit,
            requested_threshold: next,
            threshold_unit: 'plates',
            category: currentRow.category,
            justification: `Threshold change requested by ${userRole}`
          })
        });
        
        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.error || 'Failed to submit approval request');
        }
        
        alert('✅ Approval request submitted successfully! Waiting for owner approval.');
        setRows(prev => prev.map(r => (r.menu_item_id === id ? { ...r, limit: currentRow.limit } : r)));
        return;
      }
      
      const result = await WastageThresholdsApi.updateMenuItemThreshold(id, { 
        menu_item_id: id, 
        threshold_quantity: next,
        userId: userId 
      });
      const updated = result?.data || result;
      setRows(prev => prev.map(r => (r.menu_item_id === id ? { ...r, id: updated?.id || r.id, limit: next } : r)));
    } catch(e){ 
      console.error('[WastageThresholds] Error in onLimitChange:', e);
      setError(e.message);
      // Revert on error
      loadData();
    }
  };

  const onToggleActive = async (id) => {
    try {
      const curr = rows.find(r => r.menu_item_id === id);
      const newActiveState = !(curr?.is_active);
      
      // Optimistic update
      setRows(prev => prev.map(r => (r.menu_item_id === id ? { ...r, is_active: newActiveState } : r)));
      
      const result = await WastageThresholdsApi.updateMenuItemThreshold(id, { 
        menu_item_id: id, 
        is_active: newActiveState 
      });
      const updated = result?.data || result;
      setRows(prev => prev.map(r => (r.menu_item_id === id ? { ...r, id: updated?.id || r.id } : r)));
    } catch(e){ 
      console.error('[WastageThresholds] Error in onToggleActive:', e);
      setError(e.message);
      // Revert on error
      loadData();
    }
  };

  return (
    <div className="wt-page">
      <StandardScrollStyles />
      <div className="wt-title-wrap" style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '10' }}>
        <button 
          onClick={() => {
            if (goTo) {
              // Go back using the Inventory container with Stock Out tab
              goTo('inventory-stock-out');
            } else if (window && window.history) {
              // Fallback: browser back
              window.history.back();
            }
          }}
          style={{
            width: '32px',
            height: '32px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '20px', color: '#333' }} />
        </button>
        <h2 className="wt-title" style={{ margin: 0 }}>Wastage Customization</h2>
      </div>

      <div className="wt-toolbar" style={{ marginTop:8 }}>
        <UsageStyleDropdown
          value={category}
          onChange={setCategory}
          options={[
              'All Categories', 
              'Main Course', 
              'Mains', 
              'Starters', 
              'Appetizers',
              'Breakfast', 
              'Lunch',
              'Dinner',
              'Beverages', 
              'Desserts',
              'Snacks',
              'Soups',
              'Salads',
              'Sides',
              'Specials',
              'Seasonal Items'
            ]}
            placeholder="Select Category"
          />
        <div className="wt-toolbar-row2">
          <StandardSearch
            value={query}
            onChange={setQuery}
            onClear={() => setQuery('')}
            placeholder="Search Items"
            inputRef={searchInputRef}
          />
          <div />
        </div>
      </div>

      <div className="wt-content">
        <div className="wt-thead">
          <div>S. No.</div>
          <div>Menu Item</div>
          {/* Header aligned with numeric value by reserving suffix width ("plates") */}
          <div>
            <span className="wt-limit-header">
              <span className="wt-limit-label">Item Limit</span>
              <span className="wt-limit-spacer" aria-hidden="true"></span>
            </span>
          </div>
          <div style={{ textAlign:'center' }}>{/* Toggle header (no text as requested) */}</div>
        </div>
        <div className={'wt-scrollable-list'}>
          {filtered.map((r, idx) => (
            <div key={r.menu_item_id} className="wt-row">
              <div>{idx + 1}.</div>
              <div className="wt-cell">
                <div className="wt-item-name" title={r.name}>{r.name}</div>
              </div>
              <div className="wt-limit">
                <input
                  type="number"
                  value={r.limit}
                  min={0}
                  onChange={(e) => onLimitChange(r.menu_item_id, Number(e.target.value))}
                  onInput={(e) => {
                    // Strip leading zeros as the user types
                    const raw = e.target.value || '';
                    const cleaned = raw.replace(/^0+(\d)/, '$1');
                    if (cleaned !== raw) {
                      e.target.value = cleaned;
                    }
                  }}
                  onFocus={(e) => {
                    // Clear the field for fresh entry but remember previous
                    try {
                      e.target.dataset.prev = e.target.value;
                      e.target.value = '';
                    } catch {}
                  }}
                  onBlur={(e) => {
                    // If left empty, restore previous; else commit cleaned number
                    const raw = e.target.value;
                    if (raw === '' || raw == null) {
                      const prev = e.target.dataset.prev ?? '';
                      e.target.value = prev;
                      return;
                    }
                    const num = Number(raw);
                    if (!Number.isNaN(num) && num >= 0) {
                      onLimitChange(r.menu_item_id, num);
                    } else {
                      // invalid input: restore previous
                      const prev = e.target.dataset.prev ?? String(r.limit);
                      e.target.value = prev;
                    }
                  }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  size={(String(r.limit).length || 1) + 0}
                />
                <span style={{ marginLeft:0 }}>plates</span>
              </div>
              <div style={{ textAlign:'center', display:'flex', justifyContent:'center', alignItems:'center', minHeight:'40px' }}>
                <InlineToggle value={r.is_active} onChange={() => onToggleActive(r.menu_item_id)} />
              </div>
            </div>
          ))}
          {filtered.length === 0 && !loading && (
            <div style={{ padding:16, textAlign:'center', color:'#777' }}>No Items Found</div>
          )}
          {loading && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              padding: '40px 20px',
              gap: '16px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{
                color: '#666',
                fontSize: '16px',
                textAlign: 'center',
                margin: 0
              }}>Loading...</p>
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
          )}
        </div>
      </div>
  {error && <div style={{ marginTop:16, color:'#c0392b' }}>Error: {error}</div>}
    </div>
  );
}
